import type { TFunction } from 'i18next'
import {
  Play, FileText, ExternalLink, Image as ImageIcon, ListChecks, Paperclip, type LucideIcon,
} from 'lucide-react'
import type {
  CourseDetail, CourseEnrollment, CourseLesson, LessonType,
} from '@/types/courses'
import { matchExternalVideo } from '@/lib/externalVideo'

export const LESSON_ICONS: Record<LessonType, LucideIcon> = {
  video: Play,
  text: FileText,
  link: ExternalLink,
  image: ImageIcon,
  quiz: ListChecks,
  file: Paperclip,
}

/** Whether the person is a student of the course. A cancelled enrollment is not one. */
export const isEnrolled = (enrollment: CourseEnrollment | null | undefined): boolean =>
  enrollment?.status === 'enrolled' || enrollment?.status === 'completed'

/**
 * Total duration for a card or a ficha. `null` means the server could not add it up
 * (some lesson has no readable duration), and the caller shows the course without one.
 */
export const formatCourseDuration = (seconds: number | null | undefined, t: TFunction): string | null => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null
  const totalMinutes = Math.max(1, Math.round(seconds / 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return t('durationMinutes', { minutes })
  return minutes === 0
    ? t('durationHours', { hours })
    : t('durationHoursMinutes', { hours, minutes })
}

/**
 * Sum of the lesson lengths in seconds, or `null` when any one is unreadable. The same rule
 * the list endpoint applies to `durationSeconds`, for the page that has the lessons at hand.
 */
export const lessonsDurationSeconds = (lessons: CourseLesson[]): number | null => {
  let total = 0
  for (const lesson of lessons) {
    const match = lesson.duration?.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
    if (!match) return null
    const [first, second, third] = [Number(match[1]), Number(match[2]), match[3] === undefined ? null : Number(match[3])]
    total += third === null ? first * 60 + second : first * 3600 + second * 60 + third
  }
  return total
}

/** A lesson length is `mm:ss` or `hh:mm:ss`; anything else is not shown. */
export const lessonDuration = (duration: string | null | undefined): string | null =>
  duration && /^\d{1,2}(:\d{2}){1,2}$/.test(duration) ? duration : null

/** Only `http(s)` URLs are ever put in an `href` or a `src`. */
export const safeHttpUrl = (value: string | null | undefined): string | null => {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

const isHostOrSubdomain = (hostname: string, domain: string) =>
  hostname === domain || hostname.endsWith(`.${domain}`)

const ID = /^[\w-]{6,}$/

/**
 * The iframe address for a video lesson, or `null` when it is not YouTube, Vimeo or Loom.
 *
 * The host is compared exactly (or as a dotted subdomain), never by substring: the same
 * rule the API applies when it saves the lesson. The result is built from the video id,
 * so nothing from the original query string reaches the iframe.
 */
export const videoEmbedUrl = (value: string | null | undefined): string | null => {
  const safe = safeHttpUrl(value)
  if (!safe) return null
  const url = new URL(safe)
  const host = url.hostname.toLowerCase()
  const parts = url.pathname.split('/').filter(Boolean)

  if (host === 'youtu.be') {
    return ID.test(parts[0] || '') ? `https://www.youtube-nocookie.com/embed/${parts[0]}` : null
  }
  if (isHostOrSubdomain(host, 'youtube.com') || isHostOrSubdomain(host, 'youtube-nocookie.com')) {
    const id = parts[0] === 'watch'
      ? url.searchParams.get('v') || ''
      : ['embed', 'shorts', 'live', 'v'].includes(parts[0]) ? parts[1] || '' : ''
    return ID.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null
  }
  if (isHostOrSubdomain(host, 'vimeo.com')) {
    const id = parts.find((part) => /^\d+$/.test(part))
    return id ? `https://player.vimeo.com/video/${id}` : null
  }
  if (isHostOrSubdomain(host, 'loom.com')) {
    const index = parts.findIndex((part) => part === 'share' || part === 'embed')
    const id = index >= 0 ? parts[index + 1] : ''
    return ID.test(id || '') ? `https://www.loom.com/embed/${id}` : null
  }
  return null
}

/**
 * Whether a link can be a video lesson: YouTube, Vimeo, Loom (`videoEmbedUrl`) or, since F-197,
 * Bunny Stream, Cloudflare Stream, Panda Video and an HTTPS HLS manifest (the API's same rule).
 */
export const isLessonVideoUrl = (value: string | null | undefined): boolean =>
  Boolean(videoEmbedUrl(value)) || Boolean(matchExternalVideo(value)?.valid)

/** Lessons in reading order. */
export const flatLessons = (course: Pick<CourseDetail, 'modules'>): CourseLesson[] =>
  course.modules.flatMap((module) => module.courseLessons)

/** Whether the lesson has something to show to this viewer, by its type. */
export const lessonHasBody = (lesson: CourseLesson): boolean =>
  lesson.hasMedia ? true : lesson.type === 'file' ? false : lesson.type === 'quiz' ? Boolean(lesson.quiz?.questions.length) : lesson.type === 'text' ? Boolean(lesson.body?.trim()) : Boolean(lesson.contentUrl?.trim())

/** A URL-safe slug from a title: lowercase, no accents, hyphens between words. */
export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** `mm:ss` (minutes may pass 59) or `hh:mm:ss`, the same shapes the API accepts. */
export const DURATION_PATTERN = /^(?:\d{1,3}:[0-5]\d|\d{1,2}:[0-5]\d:[0-5]\d)$/

/** Plain text of an HTML description, for card previews. Parsed inertly, nothing loads. */
export const htmlToText = (html: string | null | undefined): string => {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('br, p, li, h1, h2, h3, h4, h5, h6, blockquote').forEach((node) => node.append(' '))
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

/** Whether a description is the rich editor's HTML or an older plain text. */
export const looksLikeHtml = (value: string): boolean => /<\/?[a-z][\s\S]*>/i.test(value)

export interface ApiFormErrors {
  /** Messages keyed by the API's `errors[].param`. */
  fields: Record<string, string>
  /** What to show when nothing maps to a field. */
  message: string | null
}

/**
 * Reads a failed API call. `errors[]` may be missing (a 403 from `hasRole` comes without
 * it), so the top-level `message` is the fallback and `errors[0]` is never assumed.
 */
export const readApiErrors = (error: unknown): ApiFormErrors => {
  const data = (error as { response?: { data?: { message?: unknown; errors?: unknown } } })?.response?.data
  const fields: Record<string, string> = {}
  if (Array.isArray(data?.errors)) {
    for (const item of data.errors as Array<{ param?: unknown; message?: unknown }>) {
      if (typeof item?.param === 'string' && typeof item?.message === 'string' && !fields[item.param]) {
        fields[item.param] = item.message
      }
    }
  }
  return { fields, message: typeof data?.message === 'string' ? data.message : null }
}

/** Lessons that a student could not open yet: nothing to read, watch or open. */
export const emptyLessonsCount = (course: Pick<CourseDetail, 'modules'>): number =>
  flatLessons(course).filter((lesson) => !lesson.hasContent).length

/** F-06 4A: a locked lesson comes without content and cannot be opened. Staff never get one. */
export const isLessonLocked = (lesson: Pick<CourseLesson, 'locked'>): boolean => lesson.locked === true

/**
 * The lesson "Continue" opens: where the student stopped, unless it is locked (the sequence or a
 * module date may have closed it since), then the first open lesson not finished yet, then the first open one.
 */
export const entryLesson = (lessons: CourseLesson[], lastLessonId: string | null | undefined): CourseLesson | undefined => {
  const open = lessons.filter((lesson) => !isLessonLocked(lesson))
  return open.find((lesson) => lesson.id === lastLessonId)
    ?? open.find((lesson) => !lesson.userProgress?.completed)
    ?? open[0]
}

/** The next lesson after `index` that can be opened; locked ones are skipped, never offered. */
export const nextOpenLesson = (lessons: CourseLesson[], index: number): CourseLesson | undefined =>
  lessons.slice(index + 1).find((lesson) => !isLessonLocked(lesson))

/** The closest lesson before `index` that can be opened; locked ones are skipped, never offered. */
export const previousOpenLesson = (lessons: CourseLesson[], index: number): CourseLesson | undefined =>
  lessons.slice(0, Math.max(index, 0)).reverse().find((lesson) => !isLessonLocked(lesson))

/** `MM:SS`, or `H:MM:SS` from an hour up. */
export const clock = (total: number) => {
  const seconds = Math.max(0, Math.floor(total))
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = String(seconds % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${String(m).padStart(2, '0')}:${s}`
}
